import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import LeafletMap from "@/components/LeafletMap";
import { Bus, Clock, MapPin, Sparkles, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_BANNER = {
  ON_TIME: { color: "bg-green-500", text: "text-white", title: "À l'heure" },
  LATE_1_MIN: { color: "bg-yellow-400", text: "text-zinc-900", title: "Léger retard" },
  LATE_3_MIN: { color: "bg-orange-500", text: "text-white", title: "Retard important" },
  CRITICAL: { color: "bg-red-500", text: "text-white", title: "Retard critique" },
};

export default function ClientBusLive() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/logibus/live/client-view/${token}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Lien client invalide ou expiré." : "Service indisponible.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    pollRef.current = setInterval(load, 10000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-testid="client-loading">
        <Loader2 className="animate-spin" /> <span className="ml-2 font-mono text-sm">Chargement…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="client-error">
        <div className="max-w-md w-full border border-zinc-900 bg-white p-6 text-center">
          <AlertTriangle size={32} className="mx-auto text-red-500" />
          <div className="font-display font-black text-2xl tracking-tighter mt-3">Oups</div>
          <div className="text-sm text-zinc-600 mt-2">{error}</div>
        </div>
      </div>
    );
  }

  const v = data?.vehicle;
  const status = v ? (STATUS_BANNER[v.status] || STATUS_BANNER.ON_TIME) : null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="client-bus-live-page">
      <header className="bg-zinc-900 text-white border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center">
            <Bus size={18} className="text-zinc-900" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-black text-xl tracking-tighter">LOGIBUS · Live</div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-400">
              Suivi public · Morges & Rolle
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {!v && (
          <div className="border border-zinc-900 bg-white p-6 text-center" data-testid="client-no-bus">
            <Bus size={28} className="mx-auto text-zinc-400" />
            <div className="font-display font-bold text-lg mt-2">Aucun bus actif</div>
            <div className="text-sm text-zinc-500">{data?.message || "Réessayez dans quelques instants."}</div>
          </div>
        )}

        {v && (
          <>
            <div className={`border border-zinc-900 ${status.color} ${status.text} px-4 py-3`} data-testid="client-status-banner">
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase opacity-90">Statut actuel</div>
              <div className="font-display font-black text-2xl tracking-tighter">{status.title}</div>
              {v.delay_minutes > 0 && (
                <div className="text-sm font-mono mt-0.5">+{v.delay_minutes} min sur l'horaire prévu</div>
              )}
            </div>

            <div className="border border-zinc-900 bg-white">
              <div className="p-4 border-b border-zinc-900 flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center font-display font-black text-lg" data-testid="client-route-badge">
                  {v.route_short_name}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">Direction</div>
                  <div className="font-display font-bold text-base tracking-tight truncate" data-testid="client-headsign">{v.headsign || "—"}</div>
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                <Block icon={MapPin} label="Prochain arrêt" value={v.next_stop_name || "—"} />
                <Block icon={Clock} label="Prévu" value={v.scheduled || "—"} mono />
                <Block icon={Clock} label="Estimé" value={v.estimated || "—"} mono highlight={v.delay_minutes > 0} />
                <Block icon={Bus} label="Bus" value={v.label || "—"} />
              </div>
            </div>

            <div className="border border-zinc-900 overflow-hidden">
              <LeafletMap
                waypoints={[]}
                position={{ lat: v.lat, lng: v.lon }}
                height={320}
                showPath={false}
                center={[v.lat, v.lon]}
                zoom={14}
                testId="client-live-map"
              />
            </div>

            {data?.recommendation?.client_message && (
              <div className="border border-zinc-900 bg-[#FFF7D6]" data-testid="client-recommendation">
                <div className="px-3 py-1.5 bg-zinc-900 text-[#FFC60A] text-[10px] font-mono tracking-widest uppercase inline-flex items-center gap-1">
                  <Sparkles size={11} /> Information
                </div>
                <div className="p-3 text-sm">
                  {data.recommendation.client_message}
                  {data.recommendation.alternative && (
                    <div className="mt-2 border border-zinc-900 bg-white p-2 text-xs" data-testid="client-alt">
                      <span className="font-bold uppercase tracking-widest text-[10px] text-zinc-500 mr-2">Alternative</span>
                      Ligne {data.recommendation.alternative.route_short_name} → {data.recommendation.alternative.headsign}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-400 text-center pt-4 flex items-center justify-center gap-1">
          <ShieldCheck size={11} /> Vue publique · données limitées (LPD)
        </div>
      </main>
    </div>
  );
}

function Block({ icon: Icon, label, value, mono = false, highlight = false }) {
  return (
    <div className="border border-zinc-200 p-2">
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 inline-flex items-center gap-1">
        <Icon size={11} /> {label}
      </div>
      <div className={`mt-1 ${mono ? "font-mono" : "font-semibold"} ${highlight ? "text-orange-700" : "text-zinc-900"} truncate`}>{value}</div>
    </div>
  );
}
